import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, useSyncExternalStore, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { CheckCircle2, Info, X, XCircle } from 'lucide-react'
import { cx } from './cx.ts'
import { subscribeModals, topModal } from './layers.ts'

type Tone = 'ok' | 'error' | 'info'
interface ToastItem {
  id: number
  tone: Tone
  text: string
}

interface ToastApi {
  ok: (text: string) => void
  error: (text: string) => void
  info: (text: string) => void
}

const Ctx = createContext<ToastApi | null>(null)

const areaClass =
  'pointer-events-none fixed inset-x-0 top-auto bottom-0 z-50 m-0 flex w-full max-w-none flex-col items-center gap-2 overflow-visible border-0 bg-transparent p-4 pb-[max(16px,env(safe-area-inset-bottom))] sm:items-end'

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([])
  const push = useCallback((tone: Tone, text: string) => {
    const id = Date.now() + Math.random()
    setItems((xs) => [...xs.slice(-3), { id, tone, text }])
    setTimeout(() => setItems((xs) => xs.filter((x) => x.id !== id)), tone === 'error' ? 9000 : 4500)
  }, [])
  const dismiss = (id: number) => setItems((xs) => xs.filter((x) => x.id !== id))

  // A modal dialog makes the rest of the page inert: toasts outside it could be seen but not read by
  // screen readers or dismissed. While a modal is open, toasts render inside the top one.
  const modal = useSyncExternalStore(subscribeModals, topModal, () => null)

  // Without a modal, the toast area is a popover (top layer), re-shown for each new toast so it sits
  // above anything else in the top layer.
  const layer = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const el = layer.current
    if (!el || typeof el.showPopover !== 'function') return
    try {
      if (el.matches(':popover-open')) el.hidePopover()
      if (items.length > 0 && !modal) el.showPopover()
    } catch {
      // Popover API unavailable: the fixed-position fallback still shows the toasts.
    }
  }, [items, modal])

  // Stable identity, so effects that depend on the toast API do not re-run on every render.
  const api: ToastApi = useMemo(
    () => ({
      ok: (t) => push('ok', t),
      error: (t) => push('error', t),
      info: (t) => push('info', t),
    }),
    [push],
  )

  const list = items.map((t) => (
    <div
      key={t.id}
      role={t.tone === 'error' ? 'alert' : 'status'}
      className={cx(
        'pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-xl px-4 py-3 text-[14px] shadow-[var(--shadow-pop)]',
        t.tone === 'error' ? 'bg-live text-white' : 'bg-ink text-white',
      )}
    >
      {t.tone === 'error' ? (
        <XCircle className="mt-0.5 size-4 shrink-0" aria-hidden />
      ) : t.tone === 'info' ? (
        <Info className="mt-0.5 size-4 shrink-0 text-cream" aria-hidden />
      ) : (
        <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-gold-bright" aria-hidden />
      )}
      <div className="flex-1 leading-snug">{t.text}</div>
      <button
        type="button"
        aria-label="Dismiss"
        className="-my-3 -mr-3 inline-flex size-11 shrink-0 items-center justify-center rounded-full opacity-70 hover:bg-white/10 hover:opacity-100"
        onClick={() => dismiss(t.id)}
      >
        <X className="size-4" />
      </button>
    </div>
  ))

  return (
    <Ctx.Provider value={api}>
      {children}
      <div ref={layer} popover="manual" data-toasts="" className={cx(areaClass, '[&:not(:popover-open)]:flex')}>
        {modal ? null : list}
      </div>
      {modal &&
        items.length > 0 &&
        createPortal(
          <div data-toasts="" className={areaClass}>
            {list}
          </div>,
          modal.slot,
        )}
    </Ctx.Provider>
  )
}

export function useToast(): ToastApi {
  const v = useContext(Ctx)
  if (!v) throw new Error('useToast outside ToastProvider')
  return v
}
